import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';


const DepartmentProfileStep = ({ data, onComplete, onCancel }) => {
  const [formData, setFormData] = useState({
    departmentName: data?.departmentName || '',
    ministryAffiliation: data?.ministryAffiliation || '',
    departmentType: data?.departmentType || '',
    employeeCountEstimate: data?.employeeCountEstimate || '',
    primaryContactName: data?.primaryContactName || '',
    primaryContactEmail: data?.primaryContactEmail || '',
    primaryContactPhone: data?.primaryContactPhone || '',
    departmentCode: data?.departmentCode || '',
    location: data?.location || ''
  });
  const [errors, setErrors] = useState({});

  const ministryOptions = [
    { value: 'health', label: 'Department of Health' },
    { value: 'education', label: 'Department of Education' },
    { value: 'finance', label: 'Department of Finance' },
    { value: 'treasury', label: 'Department of Treasury' },
    { value: 'justice', label: 'Department of Justice and Attorney General' },
    { value: 'police', label: 'Royal Papua New Guinea Constabulary' },
    { value: 'defence', label: 'Papua New Guinea Defence Force' },
    { value: 'foreign_affairs', label: 'Department of Foreign Affairs' },
    { value: 'commerce', label: 'Department of Commerce and Industry' },
    { value: 'lands', label: 'Department of Lands and Physical Planning' },
    { value: 'agriculture', label: 'Department of Agriculture and Livestock' },
    { value: 'works', label: 'Department of Works and Highways' },
    { value: 'transport', label: 'Department of Transport' },
    { value: 'communication', label: 'Department of Information and Communication' },
    { value: 'environment', label: 'Department of Environment and Conservation' },
    { value: 'mining', label: 'Department of Mining' },
    { value: 'petroleum', label: 'Department of Petroleum and Energy' },
    { value: 'labour', label: 'Department of Labour and Industrial Relations' },
    { value: 'community', label: 'Department of Community Development' },
    { value: 'provincial', label: 'Provincial Government' },
    { value: 'other', label: 'Other Government Entity' }
  ];

  const departmentTypeOptions = [
    { value: 'national', label: 'National Department' },
    { value: 'provincial', label: 'Provincial Department' },
    { value: 'district', label: 'District Administration' },
    { value: 'statutory', label: 'Statutory Authority' },
    { value: 'agency', label: 'Government Agency' },
    { value: 'commission', label: 'Government Commission' }
  ];

  const employeeCountOptions = [
    { value: '1-50', label: '1-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-250', label: '101-250 employees' },
    { value: '251-500', label: '251-500 employees' },
    { value: '501-1000', label: '501-1,000 employees' },
    { value: '1000+', label: '1,000+ employees' }
  ];

  const locationOptions = [
    { value: 'port_moresby', label: 'Port Moresby (National Capital District)' },
    { value: 'lae', label: 'Lae (Morobe Province)' },
    { value: 'mount_hagen', label: 'Mount Hagen (Western Highlands)' },
    { value: 'madang', label: 'Madang (Madang Province)' },
    { value: 'kokopo', label: 'Kokopo (East New Britain)' },
    { value: 'goroka', label: 'Goroka (Eastern Highlands)' },
    { value: 'wewak', label: 'Wewak (East Sepik)' },
    { value: 'kimbe', label: 'Kimbe (West New Britain)' },
    { value: 'mendi', label: 'Mendi (Southern Highlands)' },
    { value: 'arawa', label: 'Arawa (Autonomous Region of Bougainville)' },
    { value: 'other', label: 'Other Location' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.departmentName?.trim()) {
      newErrors.departmentName = 'Department name is required';
    }

    if (!formData?.ministryAffiliation) {
      newErrors.ministryAffiliation = 'Ministry affiliation is required';
    }

    if (!formData?.departmentType) {
      newErrors.departmentType = 'Department type is required';
    }

    if (!formData?.employeeCountEstimate) {
      newErrors.employeeCountEstimate = 'Employee count estimate is required';
    }

    if (!formData?.primaryContactName?.trim()) {
      newErrors.primaryContactName = 'Primary contact name is required';
    }

    if (!formData?.primaryContactEmail?.trim()) {
      newErrors.primaryContactEmail = 'Primary contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.primaryContactEmail)) {
      newErrors.primaryContactEmail = 'Invalid email format';
    }

    if (!formData?.location) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.(formData);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Department Profile</h2>
        <p className="text-gray-600">
          Provide essential information about your government department to configure the system.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Department Name */}
        <Input
          label="Department Name"
          required
          value={formData?.departmentName}
          onChange={(e) => handleChange('departmentName', e?.target?.value)}
          error={errors?.departmentName}
          placeholder="e.g., Human Resources Division"
        />

        {/* Ministry Affiliation */}
        <Select
          label="Ministry Affiliation"
          required
          options={ministryOptions}
          value={formData?.ministryAffiliation}
          onChange={(value) => handleChange('ministryAffiliation', value)}
          error={errors?.ministryAffiliation}
          placeholder="Select ministry"
          searchable
        />

        {/* Department Type */}
        <Select
          label="Department Type"
          required
          options={departmentTypeOptions}
          value={formData?.departmentType}
          onChange={(value) => handleChange('departmentType', value)}
          error={errors?.departmentType}
          placeholder="Select department type"
        />

        {/* Employee Count Estimate */}
        <Select
          label="Employee Count Estimate"
          required
          options={employeeCountOptions}
          value={formData?.employeeCountEstimate}
          onChange={(value) => handleChange('employeeCountEstimate', value)}
          error={errors?.employeeCountEstimate}
          placeholder="Select employee count range"
        />

        {/* Location */}
        <Select
          label="Primary Location"
          required
          options={locationOptions}
          value={formData?.location}
          onChange={(value) => handleChange('location', value)}
          error={errors?.location}
          placeholder="Select location"
          searchable
        />

        {/* Department Code (Optional) */}
        <Input
          label="Department Code (Optional)"
          value={formData?.departmentCode}
          onChange={(e) => handleChange('departmentCode', e?.target?.value)}
          placeholder="e.g., HR-001"
          description="Internal department identifier"
        />

        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Primary Contact Information</h3>
          
          <div className="space-y-4">
            <Input
              label="Contact Name"
              required
              value={formData?.primaryContactName}
              onChange={(e) => handleChange('primaryContactName', e?.target?.value)}
              error={errors?.primaryContactName}
              placeholder="Full name"
            />

            <Input
              label="Contact Email"
              type="email"
              required
              value={formData?.primaryContactEmail}
              onChange={(e) => handleChange('primaryContactEmail', e?.target?.value)}
              error={errors?.primaryContactEmail}
              placeholder="email@department.gov.pg"
            />

            <Input
              label="Contact Phone (Optional)"
              type="tel"
              value={formData?.primaryContactPhone}
              onChange={(e) => handleChange('primaryContactPhone', e?.target?.value)}
              placeholder="+675 XXX XXXX"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            iconName="X"
          >
            Cancel Setup
          </Button>
          <Button
            type="submit"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Continue to Employee Import
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DepartmentProfileStep;